import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const empty = {
  name: "",
  kind: "custom",
  start: "",
  finish: "",
  progress: 0,
  milestone: false,
  note: "",
};

const DEP_TYPES = [
  { key: "FS", label: "Finish → Start" },
  { key: "FF", label: "Finish → Finish" },
  { key: "SS", label: "Start → Start" },
  { key: "SF", label: "Start → Finish" },
];

/** Create / edit / delete a Gantt activity (auto activities keep computed progress). */
export function ActivityDialog({ projectId, phases, kinds, activity, phaseId, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(
      activity
        ? {
            name: activity.name,
            kind: activity.kind,
            start: activity.start || "",
            finish: activity.finish || "",
            progress: activity.auto ? activity.progress : Number(activity.progress || 0),
            milestone: activity.milestone,
            note: activity.note || "",
            depends_on: activity.depends_on || [],
            dependency_types: activity.dependency_types || {},
            dependency_lags: activity.dependency_lags || {},
          }
        : empty
    );
  }, [open, activity]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    if (!form.name.trim() || !form.start || !form.finish) {
      toast.error("Name, start and finish are required");
      return;
    }
    setBusy(true);
    try {
      const body = {
        name: form.name.trim(),
        kind: form.kind,
        start: form.start,
        finish: form.finish,
        progress: Number(form.progress) || 0,
        milestone: !!form.milestone,
        depends_on: form.depends_on || [],
        dependency_types: form.dependency_types || {},
        dependency_lags: Object.fromEntries(
          Object.entries(form.dependency_lags || {}).map(([id, days]) => [id, Number(days) || 0])
        ),
        note: form.note || null,
      };
      if (activity) {
        const { data } = await api.put(
          `/projects/${projectId}/planning/activities/${activity.id}`,
          body
        );
        toast.success(
          data?.shifted
            ? `Activity updated · ${data.shifted} dependent activity(ies) shifted`
            : "Activity updated"
        );
      } else {
        await api.post(
          `/projects/${projectId}/planning/phases/${phaseId || phases[0]?.id}/activities`,
          body
        );
        toast.success("Activity created");
      }
      onOpenChange(false);
      onSaved();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${activity.name}"?`)) return;
    try {
      await api.delete(`/projects/${projectId}/planning/activities/${activity.id}`);
      toast.success("Activity deleted");
      onOpenChange(false);
      onSaved();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="activity-dialog"
        className="max-w-lg border-slate-200 bg-white text-slate-800"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-lg font-medium tracking-tight">
            {activity ? "Edit activity" : "New activity"}
          </DialogTitle>
          <DialogDescription className="text-[11.5px] text-slate-500">
            Department activities take their progress from live panel statuses; Procurement and
            Custom activities use manual progress.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Input
            data-testid="activity-name-input"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Activity name"
            className="border-slate-200 bg-[#F8FAFC] text-xs"
          />
          <div className="flex flex-wrap gap-1.5">
            {kinds.map((kind) => (
              <button
                key={kind.key}
                data-testid={`activity-kind-${kind.key}`}
                onClick={() => set("kind", kind.key)}
                className={`mono rounded-full px-2.5 py-1 text-[9.5px] uppercase tracking-wider ${
                  form.kind === kind.key
                    ? "bg-cyan-500/18 text-cyan-700"
                    : "border border-slate-200 text-slate-600 hover:bg-slate-100"
                }`}
              >
                {kind.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              data-testid="activity-start-input"
              type="date"
              value={form.start}
              onChange={(e) => set("start", e.target.value)}
              className="border-slate-200 bg-[#F8FAFC] text-xs"
            />
            <Input
              data-testid="activity-finish-input"
              type="date"
              value={form.finish}
              onChange={(e) => set("finish", e.target.value)}
              className="border-slate-200 bg-[#F8FAFC] text-xs"
            />
          </div>
          <div className="flex items-center gap-3">
            <Input
              data-testid="activity-progress-input"
              type="number"
              min={0}
              max={100}
              value={form.progress}
              onChange={(e) => set("progress", e.target.value)}
              disabled={activity?.auto}
              placeholder="Progress %"
              className="border-slate-200 bg-[#F8FAFC] text-xs"
            />
            <label className="mono flex items-center gap-2 text-[10px] uppercase tracking-wider text-slate-600">
              <input
                data-testid="activity-milestone-input"
                type="checkbox"
                checked={!!form.milestone}
                onChange={(e) => set("milestone", e.target.checked)}
              />
              Milestone
            </label>
          </div>
          {activity ? (
            <div>
              <p className="mono mb-1.5 text-[10px] uppercase tracking-wider text-slate-500">
                Depends on
              </p>
              <div className="okn-scroll max-h-32 space-y-1 overflow-y-auto">
                {(phases || []).flatMap((p) => p.activities || []).length === 0 ? (
                  <p className="text-[11px] text-slate-400">No other activities yet.</p>
                ) : null}
                {(phases || [])
                  .flatMap((p) => p.activities || [])
                  .filter((a) => a.id !== activity.id)
                  .map((a) => {
                    const linked = (form.depends_on || []).includes(a.id);
                    const type = (form.dependency_types || {})[a.id] || "FS";
                    return (
                      <div key={a.id} className="flex items-center justify-between gap-2">
                        <label className="flex min-w-0 items-center gap-2 text-[11px] text-slate-700">
                          <input
                            type="checkbox"
                            data-testid={`activity-dep-${a.id}`}
                            checked={linked}
                            onChange={(e) => {
                              set(
                                "depends_on",
                                e.target.checked
                                  ? [...(form.depends_on || []), a.id]
                                  : (form.depends_on || []).filter((id) => id !== a.id)
                              );
                              if (!e.target.checked) {
                                const next = { ...(form.dependency_types || {}) };
                                delete next[a.id];
                                set("dependency_types", next);
                              }
                            }}
                          />
                          <span className="truncate">{a.name}</span>
                        </label>
                        {linked ? (
                          <div className="flex shrink-0 items-center gap-1">
                            <select
                              data-testid={`activity-dep-type-${a.id}`}
                              value={type}
                              onChange={(e) =>
                                set("dependency_types", {
                                  ...(form.dependency_types || {}),
                                  [a.id]: e.target.value,
                                })
                              }
                              className="mono rounded-md border border-slate-200 bg-[#F8FAFC] px-1.5 py-0.5 text-[9.5px] uppercase tracking-wider text-slate-600"
                            >
                              {DEP_TYPES.map((opt) => (
                                <option key={opt.key} value={opt.key}>
                                  {opt.key} · {opt.label}
                                </option>
                              ))}
                            </select>
                            <input
                              data-testid={`activity-dep-lag-${a.id}`}
                              type="number"
                              min={-365}
                              max={365}
                              title="Lag in days (negative = lead)"
                              value={(form.dependency_lags || {})[a.id] ?? 0}
                              onChange={(e) =>
                                set("dependency_lags", {
                                  ...(form.dependency_lags || {}),
                                  [a.id]: e.target.value,
                                })
                              }
                              className="mono w-14 rounded-md border border-slate-200 bg-[#F8FAFC] px-1.5 py-0.5 text-[9.5px] text-slate-600"
                            />
                            <span className="mono text-[9px] uppercase text-slate-400">d</span>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
              </div>
            </div>
          ) : null}

          <div className="flex items-center gap-2">
            <Button
              data-testid="activity-save-button"
              onClick={save}
              disabled={busy}
              className="h-9 flex-1 rounded-full bg-cyan-600 text-[11px] uppercase tracking-wider text-white hover:bg-cyan-500"
            >
              {activity ? "Save activity" : "Create activity"}
            </Button>
            {activity ? (
              <Button
                data-testid="activity-delete-button"
                onClick={remove}
                variant="ghost"
                className="h-9 rounded-full text-red-600 hover:bg-red-50"
              >
                <Trash2 size={14} />
              </Button>
            ) : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
